import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@shared/joykit/packages/core/common/themes/shared/typography';
import { withWindow } from '@shared/utils/withWindow';
import { ButtonV2, CheckboxV2 } from '@withjoy/joykit';
import { Flex } from '@withjoy/joykit/components/Flex';
import { TextV2 } from '@withjoy/joykit/components/TextV2';
import React, { useEffect, useRef } from 'react';

type CustomCssDetectedDialogProps = Readonly<{
  isOpen: boolean;
  onRemoveFormatting: () => void;
  onKeepFormatting: () => void;
  dontShowDialogAgain: boolean;
  setDontShowDialogAgain: (value: boolean) => void;
}>;

export const CustomCssDetectedDialog = (props: CustomCssDetectedDialogProps) => {
  const { isOpen, onRemoveFormatting, onKeepFormatting, dontShowDialogAgain, setDontShowDialogAgain } = props;
  const { tq } = useTranslation('richTextEditor');
  const primaryButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      withWindow(globalWindow => {
        // make sure the primary button is mounted before focusing it
        globalWindow.setTimeout(() => {
          if (primaryButtonRef.current) {
            primaryButtonRef.current.focus();
          }
        }, 0);
      });
    }
  }, [isOpen]);

  return (
    <ResponsiveDialog
      isOpen={isOpen}
      onClose={onKeepFormatting}
      hideDivider={true}
      hasCloseButton={true}
      dialogOverrides={{
        Content: { props: { width: ['100%', pxToRem(480)], paddingTop: 0 } },
        Body: { props: { padding: `0px !important` } },
      }}
      removeBodyPadding={true}
    >
      <Flex
        flexDirection="column"
        gap={6}
        paddingLeft={8}
        paddingRight={8}
        paddingBottom={8}
        textAlign="center"
      >
        <TextV2 typographyVariant="hed6">{tq`Do you want to keep extra formatting for this text?`}</TextV2>
        <TextV2
          typographyVariant="body2"
          color="mono14"
        >
          {tq`We noticed this text includes some extra formatting. It might not match your site's style. You can keep it if it's intentional or remove it for a consistent look.`}
        </TextV2>
        <Flex
          alignItems="center"
          justifyContent="center"
        >
          <CheckboxV2
            isChecked={dontShowDialogAgain}
            onChange={e => setDontShowDialogAgain(e.target.checked)}
          >
            {tq`Don't show this message again`}
          </CheckboxV2>
        </Flex>
        <ButtonV2
          intent="newPrimary"
          onClick={onRemoveFormatting}
          shape="rounded"
          ref={primaryButtonRef}
        >
          {tq`Remove formatting`}
        </ButtonV2>
        <ButtonV2
          variant="ghost"
          onClick={onKeepFormatting}
          color="mono15"
        >
          {tq`Keep extra formatting`}
        </ButtonV2>
      </Flex>
    </ResponsiveDialog>
  );
};
